import { useState, useRef } from 'react';

export const useComponentSearch = (el?: HTMLElement | null) => {
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [totalMatches, setTotalMatches] = useState<number>(0);
  const [query, setQuery] = useState<string>('');
  const matchesRef = useRef<Element[]>([]);

  // Function to remove all <mark> tags
  const clearHighlights = () => {
    setQuery('');
    const marks = document.querySelectorAll('mark');
    marks.forEach((mark) => {
      const parent = mark.parentNode;
      if (parent) {
        parent.replaceChild(
          document.createTextNode(mark.textContent || ''),
          mark
        );
        parent.normalize();
      }
    });
    matchesRef.current = [];
    setCurrentIndex(null);
    setTotalMatches(0);
  };

  // Function to traverse and search the DOM
  const highlightMatches = (node: Node, searchTerm: string) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      const text = node.textContent;
      const matchRegex = new RegExp(`(${searchTerm})`, 'gi');

      // Check if the text node contains the search term
      if (matchRegex.test(text)) {
        const parent = node.parentNode;
        if (parent) {
          const highlightedText = text.replace(matchRegex, '<mark>$1</mark>');
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = highlightedText;

          // Replace the original text node with highlighted content
          while (tempDiv.firstChild) {
            parent.insertBefore(tempDiv.firstChild, node);
          }
          parent.removeChild(node);
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Recursively search child nodes
      Array.from(node.childNodes).forEach((child) =>
        highlightMatches(child, searchTerm)
      );
    }
  };

  const handleSearch = (query: string) => {
    clearHighlights(); // Clear existing highlights
    setQuery(query);
    if (query.trim()) {
      highlightMatches(el || document.body, query);
      matchesRef.current = Array.from(document.querySelectorAll('mark'));
      matchesRef.current.forEach((match, index) => {
        if (index === 0) {
          match.classList.add('active-highlight');
        } else {
          match.classList.remove('active-highlight');
        }
      });
      setCurrentIndex(matchesRef.current.length > 0 ? 0 : null);
      setTotalMatches(matchesRef.current.length);
    }
  };

  const goToMatch = (index: number) => {
    if (matchesRef.current.length === 0) return;

    matchesRef.current.forEach((match, i) => {
      if (i === index) {
        match.classList.add('active-highlight');
      } else {
        match.classList.remove('active-highlight');
      }
    });

    const match = matchesRef.current[index];
    if (match) {
      match.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setCurrentIndex(index);
    }
  };

  const nextMatch = () => {
    if (matchesRef.current.length === 0 || currentIndex === null) return;
    const nextIndex = (currentIndex + 1) % matchesRef.current.length;
    goToMatch(nextIndex);
  };

  const previousMatch = () => {
    if (matchesRef.current.length === 0 || currentIndex === null) return;
    const prevIndex =
      (currentIndex - 1 + matchesRef.current.length) %
      matchesRef.current.length;
    goToMatch(prevIndex);
  };

  return {
    handleSearch,
    nextMatch,
    previousMatch,
    clearHighlights,
    totalMatches,
    query,
    setQuery,
    currentIndex: currentIndex !== null ? currentIndex + 1 : 0,
  };
};
